import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { ClientInterface, ClientOption } from "../../common/interfaces";
import { extractErrorMessage } from "../../common/utils/axios.isAxiosError";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import PageToaster from "../toaster/PageToaster";

/**
 * Props
 */
interface ReceiptGenerateModalProps {
  open: boolean;
  onClose: () => void;
  /** opcional: já abrir com um cliente pré-selecionado */
  presetClient?: ClientOption | null;
}

/**
 * Util: Debounce simples
 */
function useDebounce<T>(value: T, delay = 400): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/**
 * Extrai filename de Content-Disposition; fallback para 'recibo.pdf'
 */
function getFilenameFromHeaders(headers: any, fallback = "recibo.pdf"): string {
  try {
    const cd: string | undefined =
      headers?.["content-disposition"] || headers?.get?.("content-disposition");
    if (!cd) return fallback;
    const match = /filename\*?=(?:UTF-8''|")?([^"]+)/i.exec(cd);
    if (!match?.[1]) return fallback;
    // decodifica % e remove aspas, se houver
    return decodeURIComponent(match[1].replace(/"/g, ""));
  } catch {
    return fallback;
  }
}

/**
 * Salva o Blob localmente, criando um link temporário
 */
function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "recibo.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Modal principal
 */
const ORDER_BY: "NAME" | "DATE" = "NAME";

const ReceiptGenerateModal: React.FC<ReceiptGenerateModalProps> = ({
  open,
  onClose,
  presetClient = null
}) => {
  // form
  const [clientOption, setClientOption] = useState<ClientOption | null>(null);
  const [clientId, setClientId] = useState<number | "">("");
  const [valueStr, setValueStr] = useState<string>(""); // manter como string e depois parsear
  const [paymentMethod, setPaymentMethod] = useState<string>("PIX");
  const [notes, setNotes] = useState<string>("");

  // busca clientes
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [inputValue, setInputValue] = useState("");

  // estados de UX
  const [submitting, setSubmitting] = useState(false);

  // toasts
  const containerId = "toaster-receipt-generate";
  const { autoSuccess, autoError } = usePageToast(containerId, true);

  // preset ao abrir
  useEffect(() => {
    if (!open) return;
    if (presetClient) {
      setClientOption(presetClient);
      setClientId(presetClient.client_id);
      setInputValue(presetClient.client_name);
    } else {
      setClientOption(null);
      setClientId("");
      setInputValue("");
    }
    setValueStr("");
    setPaymentMethod("PIX");
    setNotes("");
  }, [open, presetClient]);

  // carregar lista de clientes
  useEffect(() => {
    if (!open) return;
    let ignore = false;
    (async () => {
      setLoadingClients(true);
      try {
        const res = await api.get("/client", {
          params: {
            page: 1,
            limit: 8,
            sort: "ASC",
            orderBy: ORDER_BY,
            search: debouncedSearch || undefined,
            showActives: true
          }
        });
        const items: ClientInterface[] = Array.isArray(res.data?.items)
          ? res.data.items
          : [];
        const mapped: ClientOption[] = items.map((c) => ({
          client_id: c.client_id,
          client_name: c.client_name,
          client_email: c.client_email,
          client_is_company: (c as any).client_is_company,
          client_status: (c as any).client_status
        }));
        if (!ignore) setClients(mapped);
      } catch (err) {
        console.error("Erro ao buscar clientes:", err);
        if (!ignore) setClients([]);
      } finally {
        if (!ignore) setLoadingClients(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [debouncedSearch, open]);

  // value numérico (formatos com vírgula/ponto)
  const valueNumber = useMemo(() => {
    if (!valueStr.trim()) return NaN;
    const normalized = valueStr.replace(/\./g, "").replace(",", ".");
    return Number(normalized);
  }, [valueStr]);

  const isInvalid = useMemo(() => {
    if (!clientId) return true;
    if (Number.isNaN(valueNumber) || valueNumber <= 0) return true;
    return false;
  }, [clientId, valueNumber]);

  const handleSubmit = async () => {
    if (isInvalid) {
      autoError("Preencha cliente e um valor válido (> 0).");
      return;
    }

    const dto = {
      clientId: clientId as number,
      value: valueNumber,
      payment_method: paymentMethod || undefined,
      notes: notes || undefined
    };

    setSubmitting(true);
    try {
      // IMPORTANTE: responseType 'blob' para receber PDF
      const response = await api.post("/pdf/receipt", dto, {
        responseType: "blob",
        // aceita PDF
        headers: {
          Accept: "application/pdf"
        }
      });

      const filename = getFilenameFromHeaders(response.headers, "recibo.pdf");
      saveBlob(response.data as Blob, filename);
      autoSuccess("Recibo gerado com sucesso! O download foi iniciado.");
      onClose();
    } catch (error: any) {
      console.error("Erro ao gerar recibo:", error);
      const msg = extractErrorMessage(error) ?? "Falha ao gerar recibo.";
      autoError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageToaster containerId={containerId} />
      <Modal
        open={open}
        onClose={() => {
          if (!submitting) onClose();
        }}
        aria-labelledby="generate-receipt-modal"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "1px solid var(--border-strong)",
            p: 3,
            borderRadius: 2,
            boxShadow: 4,
            width: "520px",
            maxHeight: "90vh",
            overflowY: "auto"
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Gerar Recibo (PDF)
          </Typography>

          {/* CLIENTE */}
          <Autocomplete<ClientOption>
            options={clients}
            loading={loadingClients}
            value={clientOption}
            onChange={(_, newVal) => {
              setClientOption(newVal);
              setClientId(newVal ? newVal.client_id : "");
              setInputValue(newVal ? newVal.client_name : "");
            }}
            inputValue={inputValue}
            onInputChange={(_, newInput) => {
              setInputValue(newInput);
              setSearch(newInput);
            }}
            isOptionEqualToValue={(opt, val) => opt.client_id === val.client_id}
            getOptionLabel={(opt) => opt?.client_name ?? ""}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Cliente"
                placeholder="Digite para buscar..."
                fullWidth
                required
                InputLabelProps={{ shrink: !!inputValue }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingClients ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
            fullWidth
          />

          {/* VALOR */}
          <TextField
            sx={{ mt: 2 }}
            label="Valor do recibo"
            placeholder="Ex.: 100,00"
            fullWidth
            required
            value={valueStr}
            onChange={(e) => setValueStr(e.target.value)}
            helperText="Use vírgula para decimais (ex.: 1.234,56)."
          />

          {/* MÉTODO DE PAGAMENTO */}
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Método de pagamento</InputLabel>
            <Select
              label="Método de pagamento"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              <MenuItem value="PIX">PIX</MenuItem>
              <MenuItem value="Dinheiro">Dinheiro</MenuItem>
              <MenuItem value="Cartão (Crédito/Débito)">
                Cartão (Crédito/Débito)
              </MenuItem>
              <MenuItem value="Boleto">Boleto</MenuItem>
              <MenuItem value="Transferência">Transferência</MenuItem>
              <MenuItem value="">Outro / em branco</MenuItem>
            </Select>
          </FormControl>

          {/* OBSERVAÇÕES */}
          <TextField
            sx={{ mt: 2 }}
            label="Observações (opcional)"
            placeholder="Ex.: Pagamento referente ao serviço X."
            fullWidth
            multiline
            minRows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          {/* AÇÕES */}
          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
            <Button
              onClick={onClose}
              color="secondary"
              sx={{ mr: 1 }}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              disabled={submitting || isInvalid}
              startIcon={
                submitting ? <CircularProgress size={18} /> : undefined
              }
            >
              {submitting ? "Gerando..." : "Gerar Recibo"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ReceiptGenerateModal;
